import { useEffect, useState, type ComponentType } from 'react';
import { Navigate, NavLink, useParams, useSearchParams } from 'react-router';
import { Bitacora } from './Bitacora';
import { ClavesApi } from './ClavesApi';
import { Organizaciones } from './Organizaciones';
import { Usuarios } from './Usuarios';
import { Versiones } from './Versiones';
import { IcoCampana, IcoDescarga, IcoPersonas } from '../ui/iconos';
import { GrupoLado } from '../ui/Lado';
import { tx } from '../i18n';
import { pedir } from '../lib/api';
import { permisos } from '../lib/appliance';
import { useConsulta } from '../lib/consulta';
import { useSesion } from '../sesion/sesion';
import { Cabecera, Consulta, primero, useAviso } from '../ui/piezas';

type Modo = 'smtp' | 'm365';

interface CorreoGuardado {
  correo:
    | ({ modo: Modo; remitente: string } & Partial<{ host: string; puerto: number; seguro: boolean; usuario: string; tenant: string; app: string }>)
    | null;
  tiene_secreto: boolean;
}

interface Suscripcion {
  tipos: string[];
  idioma: 'en' | 'es';
  propia: boolean;
}

const TIPOS = [
  'sin_contacto',
  'disco',
  'motor_caido',
  'licencia_por_vencer',
  'licencia_vencida',
  'version_vieja',
  'actualizacion_fallida',
  'entregas_fallidas',
] as const;

type Seccion = 'organizaciones' | 'usuarios' | 'claves' | 'bitacora' | 'versiones' | 'correo' | 'avisos';
type Grupo = { id: keyof typeof tx.ajustes.grupos; Ico: ComponentType<{ className?: string }>; secciones: Seccion[] };

/**
 * Ajustes del Centro (docs/centro.md §6), con el mismo panel lateral del appliance: los
 * clientes y quién entra, las versiones que se publican y por dónde salen los avisos. Cada
 * sección tiene su dirección (/ajustes/<sección>); las pantallas que antes estaban arriba
 * (Organizaciones, Versiones, Bitácora) viven acá, y sus direcciones viejas llevan acá.
 */
export function Ajustes() {
  const { usuario } = useSesion();
  const puede = permisos(usuario?.rol);
  const [aviso, avisar] = useAviso();
  const { seccion } = useParams();
  const [params] = useSearchParams();
  const t = tx.ajustes;
  const grupos: Grupo[] = (
    [
      {
        id: 'acceso',
        Ico: IcoPersonas,
        secciones: [
          'organizaciones',
          ...(puede.manejarUsuarios ? (['usuarios'] as const) : []),
          ...(puede.manejarClaves ? (['claves'] as const) : []),
          ...(puede.verBitacora ? (['bitacora'] as const) : []),
        ],
      },
      { id: 'actualizaciones', Ico: IcoDescarga, secciones: ['versiones'] },
      { id: 'avisos', Ico: IcoCampana, secciones: [...(puede.editarAppliance ? (['correo'] as const) : []), 'avisos'] },
    ] as Grupo[]
  ).filter((g) => g.secciones.length);
  const todas = grupos.flatMap((g) => g.secciones);
  // la dirección vieja con ?pestana= lleva a la sección
  const pedida = (seccion ?? params.get('pestana')) as Seccion | null;
  const actual = pedida && todas.includes(pedida) ? pedida : null;
  if (!actual || !seccion) return <Navigate to={`/ajustes/${actual ?? todas[0]}`} replace />;

  return (
    <div className="pagina cen-ajustes">
      <MenuAjustes grupos={grupos} actual={actual} />
      <section className="cen-aj-cuerpo" aria-label={t.secciones[actual]}>
        {actual === 'organizaciones' && <Organizaciones />}
        {actual === 'usuarios' && <Usuarios />}
        {actual === 'claves' && <ClavesApi />}
        {actual === 'bitacora' && <Bitacora />}
        {actual === 'versiones' && <Versiones />}
        {(actual === 'correo' || actual === 'avisos') && (
          <div className="pagina">
            <Cabecera titulo={t.secciones[actual]} />
            {aviso}
            {actual === 'correo' ? <Correo avisar={avisar} /> : <MisAvisos avisar={avisar} />}
          </div>
        )}
      </section>
    </div>
  );
}

/** En pantalla angosta el menú es una fila de pastillas: ahí no se pliega nada. */
const esAngosta = () => window.matchMedia?.('(max-width: 860px)').matches ?? false;

/**
 * El menú, igual al del appliance: un solo grupo abierto a la vez; abrir otro cierra el que
 * estaba, y tocar el abierto lo cierra.
 */
function MenuAjustes({ grupos, actual }: { grupos: Grupo[]; actual: Seccion }) {
  const grupoActual = grupos.find((g) => g.secciones.includes(actual))?.id;
  /** Un solo grupo abierto a la vez: abrir otro cierra el que estaba. */
  const [abierto, setAbierto] = useState<string | undefined>(grupoActual);
  const [angosta, setAngosta] = useState(esAngosta);
  useEffect(() => {
    const m = window.matchMedia?.('(max-width: 860px)');
    const alCambiar = () => setAngosta(esAngosta());
    m?.addEventListener?.('change', alCambiar);
    return () => m?.removeEventListener?.('change', alCambiar);
  }, []);
  // al llegar a una sección (un enlace, el botón atrás), queda abierto su grupo: se ajusta en
  // el mismo render en que cambia, comparando con el grupo anterior
  const [grupoPrevio, setGrupoPrevio] = useState(grupoActual);
  if (grupoPrevio !== grupoActual) {
    setGrupoPrevio(grupoActual);
    setAbierto(grupoActual);
  }
  // tocar un grupo lo abre y cierra el otro; tocar el que está abierto lo cierra
  const alternar = (id: string) => setAbierto((a) => (a === id ? undefined : id));
  return (
    <nav className="lado cen-aj-nav" aria-label={tx.ajustes.nav}>
      <h1 className="cen-aj-titulo">{tx.ajustes.titulo}</h1>
      {grupos.map((g, i) => (
        <GrupoLado
          key={g.id}
          id={g.id}
          titulo={tx.ajustes.grupos[g.id]}
          Ico={g.Ico}
          n={g.secciones.length}
          abierto={angosta || g.id === abierto}
          aqui={g.id === grupoActual}
          alTocar={() => alternar(g.id)}
          orden={i}
        >
          {g.secciones.map((s) => (
            <NavLink key={s} to={`/ajustes/${s}`}>
              {tx.ajustes.secciones[s]}
            </NavLink>
          ))}
        </GrupoLado>
      ))}
    </nav>
  );
}

function Correo({ avisar }: { avisar: (t: string, error?: boolean) => void }) {
  const q = useConsulta<CorreoGuardado>('/ajustes/correo');
  // `usuario` a secas ya es el del SMTP, más abajo; este es quien está usando el panel.
  const { usuario: quienEntro } = useSesion();
  const t = tx.ajustes;
  const [modo, setModo] = useState<Modo>('smtp');
  // La prueba arranca con la dirección de quien entró, pero se puede cambiar: lo que hay que
  // ver es si el correo sale del tenant y llega afuera, no si se puede mandar a uno mismo.
  const [para, setPara] = useState(quienEntro?.correo ?? '');
  const [remitente, setRemitente] = useState('');
  const [host, setHost] = useState('');
  const [puerto, setPuerto] = useState('587');
  const [seguro, setSeguro] = useState(false);
  const [usuario, setUsuario] = useState('');
  const [tenant, setTenant] = useState('');
  const [app, setApp] = useState('');
  const [clave, setClave] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  // lo guardado llena el formulario una vez (en el render en que llega; el secreto nunca
  // vuelve de la API): se recuerda de qué respuesta se llenó para no pisar lo que se edita
  const [llenadoDe, setLlenadoDe] = useState<unknown>(null);
  const c = q.data?.correo;
  if (c && llenadoDe !== c) {
    setLlenadoDe(c);
    setModo(c.modo);
    setRemitente(c.remitente);
    setHost(c.host ?? '');
    setPuerto(String(c.puerto ?? 587));
    setSeguro(!!c.seguro);
    setUsuario(c.usuario ?? '');
    setTenant(c.tenant ?? '');
    setApp(c.app ?? '');
  }

  const probar = async () => {
    setError('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(para.trim())) return setError(t.paraInvalido);
    try {
      const r = await pedir<{ a: string }>('/ajustes/correo/probar', { metodo: 'POST', cuerpo: { para: para.trim() } });
      avisar(t.pruebaMandada(r.a));
    } catch (e) {
      avisar(e instanceof Error ? e.message : String(e), true);
    }
  };

  const guardar = async () => {
    setError('');
    const falta = primero(
      !remitente.includes('@') && t.faltaRemitente,
      modo === 'smtp' && !host.trim() && t.faltaHost,
      modo === 'm365' && !tenant.trim() && t.faltaTenant,
      modo === 'm365' && !app.trim() && t.faltaApp,
    );
    if (falta) return setError(falta);
    setGuardando(true);
    try {
      await pedir('/ajustes/correo', {
        metodo: 'PUT',
        cuerpo:
          modo === 'smtp'
            ? {
                modo,
                remitente: remitente.trim(),
                host: host.trim(),
                puerto: Number(puerto),
                seguro,
                usuario: usuario.trim() || undefined,
                clave: clave || undefined,
              }
            : { modo, remitente: remitente.trim(), tenant: tenant.trim(), app: app.trim(), clave: clave || undefined },
      });
      setClave('');
      await q.refetch();
      avisar(t.correoGuardado);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    setGuardando(false);
  };

  return (
    <section className="panel">
      <Consulta q={q}>
        <p className="nota">{t.correoQue}</p>
        <div className="seg" role="group" aria-label={t.correo}>
          {(['smtp', 'm365'] as Modo[]).map((m) => (
            <button key={m} type="button" aria-pressed={modo === m} onClick={() => setModo(m)}>
              {t.modos[m]}
            </button>
          ))}
        </div>

        <div className="campos">
          <label className="campo" htmlFor="c-remitente">
            {t.remitente}
            <input
              id="c-remitente"
              className="entrada"
              value={remitente}
              onChange={(e) => setRemitente(e.target.value)}
              placeholder={t.remitenteEjemplo}
            />
          </label>

          {modo === 'smtp' ? (
            <>
              <label className="campo" htmlFor="c-host">
                {t.servidor}
                <input id="c-host" className="entrada" value={host} onChange={(e) => setHost(e.target.value)} placeholder={t.servidorEjemplo} />
              </label>
              <label className="campo" htmlFor="c-puerto">
                {t.puerto}
                <input id="c-puerto" className="entrada" type="number" value={puerto} onChange={(e) => setPuerto(e.target.value)} />
              </label>
              <label className="campo campo-si" htmlFor="c-seguro">
                <input id="c-seguro" type="checkbox" checked={seguro} onChange={(e) => setSeguro(e.target.checked)} />
                {t.tlsDirecto}
              </label>
              <label className="campo" htmlFor="c-usuario">
                {t.usuario}
                <input id="c-usuario" className="entrada" value={usuario} onChange={(e) => setUsuario(e.target.value)} />
              </label>
            </>
          ) : (
            <>
              <label className="campo" htmlFor="c-tenant">
                {t.tenant}
                <input id="c-tenant" className="entrada" value={tenant} onChange={(e) => setTenant(e.target.value)} />
              </label>
              <label className="campo" htmlFor="c-app">
                {t.app}
                <input id="c-app" className="entrada" value={app} onChange={(e) => setApp(e.target.value)} />
              </label>
            </>
          )}

          <label className="campo" htmlFor="c-clave">
            {modo === 'smtp' ? t.clave : t.secreto}
            <input
              id="c-clave"
              className="entrada"
              type="password"
              autoComplete="new-password"
              value={clave}
              onChange={(e) => setClave(e.target.value)}
              placeholder={q.data?.tiene_secreto ? t.yaGuardada : ''}
            />
            <small className="ayuda">{q.data?.tiene_secreto ? t.vacioLaDeja : t.noSeMuestra}</small>
          </label>
        </div>

        {error && (
          <p className="error-form" role="alert">
            {error}
          </p>
        )}

        <div className="botones">
          <button type="button" className="btn prim" disabled={guardando} onClick={() => void guardar()}>
            {guardando ? tx.comun.guardando : tx.comun.guardar}
          </button>
        </div>

        <div className="fila-prueba">
          <label className="campo" htmlFor="c-para">
            {t.paraLaPrueba}
            <input id="c-para" className="entrada" type="email" value={para} onChange={(e) => setPara(e.target.value)} />
          </label>
          <button type="button" className="btn" disabled={!q.data?.correo} onClick={() => void probar()}>
            {t.probar}
          </button>
        </div>
      </Consulta>
    </section>
  );
}

function MisAvisos({ avisar }: { avisar: (t: string, error?: boolean) => void }) {
  const q = useConsulta<Suscripcion>('/avisos/suscripcion');
  const t = tx.ajustes;
  const [tipos, setTipos] = useState<string[] | null>(null);
  const [idioma, setIdioma] = useState<'en' | 'es'>('es');
  // lo guardado llena el formulario en el render en que llega (una vez por respuesta)
  const [llenadoDe, setLlenadoDe] = useState<unknown>(null);
  if (q.data && llenadoDe !== q.data) {
    setLlenadoDe(q.data);
    setTipos(q.data.tipos);
    setIdioma(q.data.idioma);
  }
  const elegidos = tipos ?? [];

  return (
    <section className="panel">
      <Consulta q={q}>
        <p className="nota">{t.misAvisosQue}</p>
        <ul className="tipos-aviso">
          {TIPOS.map((tipo) => (
            <li key={tipo}>
              <label className="campo campo-si" htmlFor={`t-${tipo}`}>
                <input
                  id={`t-${tipo}`}
                  type="checkbox"
                  checked={elegidos.includes(tipo)}
                  onChange={(e) => setTipos(e.target.checked ? [...elegidos, tipo] : elegidos.filter((x) => x !== tipo))}
                />
                {tx.avisos[tipo] ?? tipo}
              </label>
            </li>
          ))}
        </ul>
        <label className="campo" htmlFor="a-idioma">
          {t.idiomaDelCorreo}
          <select id="a-idioma" className="lista-sel" value={idioma} onChange={(e) => setIdioma(e.target.value as 'en' | 'es')}>
            <option value="es">Español</option>
            <option value="en">English</option>
          </select>
        </label>
        <div className="botones">
          <button
            type="button"
            className="btn prim"
            onClick={() =>
              void pedir('/avisos/suscripcion', { metodo: 'PUT', cuerpo: { tipos: elegidos, idioma } })
                .then(() => avisar(t.suscripcionGuardada))
                .catch((e: Error) => avisar(e.message, true))
            }
          >
            {tx.comun.guardar}
          </button>
          {q.data && !q.data.propia && <span className="nota">{t.porOmision}</span>}
        </div>
      </Consulta>
    </section>
  );
}
